<template>
    <div class="teams-list">
        <!-- Section: Moje týmy (only for authenticated users) -->
        <div v-if="userStore.isAuthenticated" class="ios-teams-section">
            <div class="ios-section-header">
                <span class="ios-section-title">{{ t('myTeams') }}</span>
            </div>
            <div v-if="props.articles.length === 0" class="section-content">
                <div class="empty-state">{{ t('notInAnyTeam') }}</div>
            </div>
            <div v-else class="section-content" :class="{ 'section-content--menu-active': menuOpenCount > 0 }">
                <SectionRow v-for="(article, index) in props.articles" :key="article._id || index"
                    :can-edit="hasPermission(article)"
                    :is-last="index === props.articles.length - 1" @click="navigateToTeam(article)"
                    @menu-open="menuOpenCount++" @menu-close="menuOpenCount--">
                    <div class="ios-row-icon">
                        <img :src="`${UPLOADS}/teams/w75-${article.imageUrl}`" :alt="article.name" />
                    </div>
                    <div class="ios-row-info">
                        <div class="ios-row-name">
                            {{ article.name }}
                            <v-icon v-if="(article as any).isPrivate" size="14" class="lock-icon">mdi-lock</v-icon>
                        </div>
                        <div class="compact-date" v-if="getTeamSubtitle(article)">
                            {{ getTeamSubtitle(article) }}
                        </div>
                        <div class="age-chip-row">
                            <span class="age-chip">{{ getAgeGroupLabel(article) }}</span>
                        </div>
                        <div class="role-chips" v-if="getRoleChips(article).length">
                            <span v-for="chip in getRoleChips(article)" :key="chip.key" class="role-chip">{{ chip.label
                                }}</span>
                        </div>
                    </div>

                    <template #menu="{ close }">
                        <div class="context-menu-item" @click="openEditModal(article); close()">
                            <span>{{ t('nav.teamSettings') }}</span>
                            <v-icon size="18">mdi-cog-outline</v-icon>
                        </div>
                    </template>
                </SectionRow>
            </div>
        </div>

        <!-- Section: Veřejné týmy -->
        <div v-if="!userStore.isAuthenticated || !!props.searchQuery" class="ios-teams-section">
            <div class="ios-section-header">
            <span class="ios-section-title">{{ userStore.isAuthenticated ? t('otherTeams') : t('teams') }}</span>
            </div>
            <div v-if="props.publicTeams.length === 0" class="section-content">
                <div class="empty-state">{{ t('noTeams') }}</div>
            </div>
            <div v-else class="section-content" :class="{ 'section-content--menu-active': menuOpenCount > 0 }">
                <SectionRow v-for="(team, index) in props.publicTeams" :key="'pub-' + (team._id || index)"
                    :can-edit="isGlobalAdmin"
                    :is-last="!props.hasMorePublicTeams && index === props.publicTeams.length - 1"
                    @click="navigateToTeam(team)" @menu-open="menuOpenCount++" @menu-close="menuOpenCount--">
                    <div class="ios-row-icon">
                        <img v-if="team.imageUrl" :src="`${UPLOADS}/teams/w75-${team.imageUrl}`" :alt="team.name" />
                        <v-icon v-else size="24" color="grey">mdi-shield-outline</v-icon>
                    </div>
                    <div class="ios-row-info">
                        <div class="ios-row-name">
                            {{ team.name }}
                            <v-icon v-if="(team as any).isPrivate" size="14" class="lock-icon">mdi-lock</v-icon>
                        </div>
                        <div class="compact-date" v-if="getTeamSubtitle(team)">
                            {{ getTeamSubtitle(team) }}
                        </div>
                        <div class="age-chip-row">
                            <span class="age-chip">{{ getAgeGroupLabel(team) }}</span>
                        </div>
                    </div>

                    <template #menu="{ close }">
                        <div class="context-menu-item" @click="openEditModal(team); close()">
                            <span>{{ t('nav.teamSettings') }}</span>
                            <v-icon size="18">mdi-cog-outline</v-icon>
                        </div>
                    </template>
                </SectionRow>

            </div>

            <!-- Load more row -->
            <div v-if="props.hasMorePublicTeams" style="padding: 0 16px; margin-top: 8px;">
                <button class="load-more-btn" :disabled="props.publicTeamsLoading" @click="emit('load-more-public')">
                    <v-progress-circular v-if="props.publicTeamsLoading" indeterminate size="16" width="2" />
                    <template v-else>{{ t('loadMore') }}</template>
                </button>
            </div>
        </div>
    </div>

    <!-- Edit Team Modal -->
    <TeamFormModal v-model="showEditModal" :teamId="editingTeamId" @saved="onTeamUpdated" @deleted="onTeamDeletedFromModal" />

    <!-- Team Members Modal -->
    <TeamMembersModal v-model="showMembersModal" :teamId="membersTeamId" :readOnly="membersReadOnly"
        @updated="onTeamUpdated" />

    <!-- Price List Dialog -->
    <PriceListDialog v-model="showPriceListModal" :teamId="priceListTeamId" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ITeam } from '@/types'
import { useUserStore } from '@/stores/user/userStore'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axiosInstance from '@/services/axiosInstance'
import TeamFormModal from '@/components/Team/TeamFormModal.vue'
import TeamMembersModal from '@/components/Team/TeamMembersModal.vue'
import PriceListDialog from '@/components/Team/PriceListDialog.vue'
import SectionRow from '@/components/SectionRow.vue'

const { t } = useI18n()
const UPLOADS = import.meta.env.VITE_UPLOADS
const router = useRouter()
const userStore = useUserStore()

const props = defineProps<{
    articles: ITeam[]
    publicTeams: ITeam[]
    publicTeamsLoading: boolean
    hasMorePublicTeams: boolean
    searchQuery?: string
}>()

const emit = defineEmits(['updated', 'deleted', 'load-more-public'])

// Menu open tracking for overflow control
const menuOpenCount = ref(0)

// Global admin check
const isGlobalAdmin = computed(() => {
    const roles = userStore.user?.userRoles || []
    return roles.includes('administrator')
})

// Edit modal state
const showEditModal = ref(false)
const editingTeamId = ref<string | undefined>(undefined)

// Members modal state
const showMembersModal = ref(false)
const membersTeamId = ref<string>('')
const membersReadOnly = ref(false)

// Price list modal state
const showPriceListModal = ref(false)
const priceListTeamId = ref<string>('')

// Check if user has permission to edit/delete team
const hasPermission = (team: ITeam) => {
    const userId = userStore.user?._id
    const roles = userStore.user?.userRoles || []

    if (roles.includes('administrator')) return true
    if (!userId) return false

    // Check if user is admin of this team
    const admins = team.admins || []
    return admins.some((admin: any) =>
        typeof admin === 'string' ? admin === userId : admin._id === userId
    )
}

const isMember = (team: ITeam) => {
    const userId = userStore.user?._id
    if (!userId) return false

    const checkArray = (arr: any[]) => arr?.some((item: any) =>
        typeof item === 'string' ? item === userId : item._id === userId
    ) || false

    return checkArray(team.players) || checkArray(team.goalkeepers) ||
        checkArray((team as any).coaches) || checkArray((team as any).treasurers) ||
        checkArray((team as any).captains) || checkArray((team as any).externalOrganizers)
}

const getTeamLocation = (team: ITeam): string | null => {
    const loc = team.location
    if (!loc) return null
    if (typeof loc === 'string') return loc || null
    if (typeof loc === 'object' && (loc as any).city) return (loc as any).city
    if (typeof loc === 'object' && (loc as any).label) return (loc as any).label
    return null
}

const ageGroupKeys: Record<string, string> = {
    preschool: 'ageGroup_preschool',
    youngerJuniors: 'ageGroup_youngerJuniors',
    olderJuniors: 'ageGroup_olderJuniors',
    women: 'ageGroup_women',
    youth: 'ageGroup_youth',
    juniors: 'ageGroup_juniors',
    seniorsUnregistered: 'ageGroup_seniorsUnregistered',
    seniors: 'ageGroup_seniors',
}

const getTeamSubtitle = (team: ITeam) => {
    const parts: string[] = []
    const city = getTeamLocation(team)
    if (city) parts.push(city)
    const playerCount = (team.players?.length || 0) + (team.goalkeepers?.length || 0)
    if (playerCount > 0) parts.push(t('nPlayers', { count: playerCount }))
    return parts.join(' · ') || null
}

const getAgeGroupLabel = (team: ITeam): string => {
    const ag = (team as any).ageGroup
    const key = ag && ageGroupKeys[ag] ? ageGroupKeys[ag] : ageGroupKeys['seniors']
    return t(key)
}

const roleKeys: Record<string, string> = {
    admins: 'teamPage.roleAdmins',
    players: 'teamPage.rolePlayers',
    goalkeepers: 'teamPage.rolePlayers',
    treasurers: 'teamPage.roleTreasurers',
    captains: 'teamPage.roleCaptains',
}

const getRoleChips = (team: any) => {
    const matched: string[] = team.matchedRoles || []
    const chips: { key: string; label: string }[] = []
    const seen = new Set<string>()

    for (const role of matched) {
        const key = roleKeys[role]
        if (key) {
            const label = t(key)
            if (!seen.has(label)) {
                seen.add(label)
                chips.push({ key: role, label })
            }
        }
    }
    return chips
}

const navigateToTeam = (team: ITeam) => {
    router.push(`/teams/${team._id}`)
}

const openEditModal = (team: ITeam) => {
    editingTeamId.value = team._id
    showEditModal.value = true
}

const openMembersModal = (team: ITeam, readOnly = false) => {
    membersTeamId.value = team._id || ''
    membersReadOnly.value = readOnly
    showMembersModal.value = true
}

const openPriceListModal = (team: ITeam) => {
    priceListTeamId.value = team._id || ''
    showPriceListModal.value = true
}

const onTeamUpdated = () => {
    emit('updated')
}

const onTeamDeletedFromModal = (teamId: string) => {
    emit('deleted', teamId)
}
</script>

<style lang="scss" scoped>
.teams-list {
    padding: 0;
}

.ios-teams-section {
    margin-bottom: 24px;
}

.section-title {
    display: block;
    font-size: 1.17em;
    font-weight: 600;
    color: rgb(var(--v-theme-sheetTextSecondary));
    padding: 0 16px;
    margin: 0 0 8px 0;
}

.section-row-wrapper {
    position: relative;
}

.empty-state {
    padding: 20px 16px;
    text-align: center;
    color: rgba(var(--v-theme-sheetText), 0.5);
    font-size: 15px;
}

.age-chip-row {
    margin-top: 4px;
}

.role-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
}

.age-chip {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(var(--v-theme-sheetText), 0.08);
    color: rgba(var(--v-theme-sheetText), 0.7);
    line-height: 18px;
}

.role-chip {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(var(--v-theme-ice), 0.1);
    color: rgb(var(--v-theme-ice));
    border: 1px solid rgba(var(--v-theme-ice), 0.2);
    line-height: 18px;
}

.lock-icon {
    margin-left: 4px;
    opacity: 0.5;
    vertical-align: middle;
}

// Delete confirm dialog
.delete-confirm-dialog {
    padding: 24px;
    text-align: center;
}

.delete-confirm-icon {
    margin-bottom: 16px;
}

.delete-confirm-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.delete-confirm-subtitle {
    font-size: 0.9rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-bottom: 24px;
}

.delete-confirm-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.delete-confirm-btn {
    padding: 10px 24px;
    border-radius: 12px;
    border: none;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &--cancel {
        background: rgba(var(--v-theme-on-surface), 0.1);
        color: inherit;
    }

    &--confirm {
        background: rgb(var(--v-theme-error));
        color: white;

        &:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }
    }

    &:active:not(:disabled) {
        transform: scale(0.96);
    }
}
</style>
